<template>
  <div>
    <v-row no-gutters>
      <v-text-field
        dense
        outlined
        label="Empfänger"
        disabled
        :value="user.email"
      >
        <template v-slot:append-outer>
          <v-btn
            :disabled="additionalRecipients.length > 0"
            icon
            color="success"
            @click="addRecipient"
          >
            <v-icon>{{ icons.mdiPlus }}</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </v-row>
    <v-row
      v-for="(recipient, index) in additionalRecipients"
      :key="`recipient-${index}`"
      no-gutters
    >
      <v-text-field
        v-model="recipient.email"
        dense
        outlined
        :label="`Empfänger ${index + 1}`"
        :rules="[rules.email]"
      >
        <template v-slot:append-outer>
          <v-btn icon color="success" @click="addRecipient(recipient)">
            <v-icon>{{ icons.mdiPlus }}</v-icon>
          </v-btn>
          <v-btn icon color="error" @click="removeRecipient(recipient)">
            <v-icon>{{ icons.mdiMinus }}</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </v-row>
    <v-text-field
      dense
      outlined
      label="Betreff"
      v-model="subject"
    ></v-text-field>
    <v-select
      dense
      outlined
      label="Ansprache"
      :items="ansprachen"
      v-model="ansprache"
    ></v-select>
    <p v-if="ansprache === 'sie'" class="text--disabled">
      {{ generateSalutationLine(user) }},
    </p>
    <v-text-field
      v-else
      dense
      outlined
      label="Persönliche Ansprache"
      v-model="anspracheText"
    ></v-text-field>
    <div class="mb-4">
      <ckeditor
        :editor="editor"
        :config="editorConfig"
        v-model="message"
      ></ckeditor>
    </div>
    <v-text-field
      dense
      outlined
      hide-details
      label="Gruß"
      placeholder="Mit freundlichen Grüßen"
      v-model="grussText"
    ></v-text-field>
    <p class="text--disabled mt-2">
      {{ authUser.vorname }} {{ authUser.nachname }}
    </p>
    <p class="text--disabled mb-0">Telefon: {{ authUser.telefonnr }}</p>
    <p v-if="authUser.mobilnr" class="text--disabled mb-0">
      Mobil: {{ authUser.mobilnr }}
    </p>
    <p class="text--disabled mb-6">E-Mail: {{ authUser.email }}</p>
    <v-textarea
      auto-grow
      dense
      outlined
      label="Zusatztext"
      v-model="additionalText"
      rows="3"
    ></v-textarea>
  </div>
</template>

<script>
import { mdiMinus, mdiPlus } from "@mdi/js";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import CKEditor from "@ckeditor/ckeditor5-vue2";
import AddressFormatter from "@/mixins/AddressFormatter";

export default {
  name: "FormNewMail",
  components: {
    ckeditor: CKEditor.component,
  },
  props: {
    user: {
      type: Object,
      default: () => {},
      required: true,
    },
    initialSubject: {
      type: String,
      default: "",
    },
    initialAnsprache: {
      type: String,
      default: "sie",
    },
    initialAnspracheText: {
      type: String,
      default: "",
    },
    initialMessage: {
      type: String,
      default: "",
    },
    initialGrussText: {
      type: String,
      default: "",
    },
    initialAdditionalText: {
      type: String,
      default: "",
    },
  },
  mixins: [AddressFormatter],
  data() {
    return {
      editor: Editor,
      editorConfig: {
        toolbar: [
          "bold",
          "italic",
          "|",
          "bulletedList",
          "numberedList",
          "|",
          "undo",
          "redo",
        ],
      },
      additionalRecipients: [],
      subject: this.initialSubject,
      ansprachen: [
        { text: "Persönlich (Du)", value: "du" },
        { text: "Förmlich (Sie)", value: "sie" },
      ],
      ansprache: this.initialAnsprache,
      anspracheText: this.initialAnspracheText,
      message: this.initialMessage,
      grussText: this.initialGrussText,
      additionalText: this.initialAdditionalText,
      icons: {
        mdiPlus,
        mdiMinus,
      },
      rules: {
        required: (value) => !!value || "Dies ist ein Pflichtfeld.",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Keine gültige E-Mail Adresse.";
        },
      },
    };
  },
  computed: {
    authUser() {
      return this.$store.state.user.authUser;
    },
    formData() {
      return {
        additionalRecipients: this.additionalRecipients,
        subject: this.subject,
        ansprache: this.ansprache,
        anspracheText: this.anspracheText,
        message: this.message,
        grussText: this.grussText,
        additionalText: this.additionalText,
      };
    },
  },
  watch: {
    formData: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true,
      immediate: true,
    },
    initialSubject(newVal) {
      this.subject = newVal;
    },
    initialAnsprache(newVal) {
      this.ansprache = newVal;
    },
    initialAnspracheText(newVal) {
      this.anspracheText = newVal;
    },
    initialMessage(newVal) {
      this.message = newVal;
    },
    initialGrussText(newVal) {
      this.grussText = newVal;
    },
    initialAdditionalText(newVal) {
      this.additionalText = newVal;
    },
  },
  methods: {
    addRecipient() {
      if (this.additionalRecipients.length <= 2) {
        this.additionalRecipients.push({ email: "" });
      } else {
        const notification = {
          type: "warning",
          title: "Maximale Empfänger erreicht.",
          message: "Es können maximal 3 weitere Empfänger hinzugefügt werden.",
        };

        this.$store.commit("notifications/ADD", notification);
      }
    },
    removeRecipient(recipient) {
      this.additionalRecipients.splice(
        this.additionalRecipients.indexOf(recipient),
        1
      );
    },
  },
};
</script>

<style scoped></style>
